import { Link } from "@/lib/router-compat";
import { motion } from "framer-motion";
import { Flame, Sun, Sofa, Wine, GlassWater, Sparkles, Users, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRef, useState, useMemo, type CSSProperties } from "react";
import { useMarqueeDuration } from "@/hooks/useMarqueeDuration";

const MARQUEE_SPEED = 20; // px per second — slow luxury pace

interface Experience {
  icon: LucideIcon;
  labelKey: string;
  slug: string;
  taglineKey: string;
}

const experiences: Experience[] = [
  { icon: Flame, labelKey: "landing.fine_dining", slug: "fine-dining", taglineKey: "landing.fine_dining_tagline" },
  { icon: Sun, labelKey: "landing.rooftop", slug: "rooftop", taglineKey: "landing.rooftop_tagline" },
  { icon: Sofa, labelKey: "landing.cozy", slug: "cozy", taglineKey: "landing.cozy_tagline" },
  { icon: Wine, labelKey: "landing.wine", slug: "wine", taglineKey: "landing.wine_tagline" },
  { icon: GlassWater, labelKey: "landing.lounge", slug: "lounge", taglineKey: "landing.lounge_tagline" },
  { icon: Users, labelKey: "landing.social_clubs", slug: "social-clubs", taglineKey: "landing.social_clubs_tagline" },
  { icon: Sparkles, labelKey: "landing.special_events", slug: "special-events", taglineKey: "landing.special_events_tagline" },
];

export const ExperienceNav = () => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const items = useMemo(() => [...experiences, ...experiences], []);
  const duration = useMarqueeDuration(trackRef as React.RefObject<HTMLElement>, 2, MARQUEE_SPEED, items.length);

  return (
    <section className="py-20 md:py-24">
      <div className="max-w-5xl mx-auto px-4 text-center mb-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="font-wordmark-glow text-2xl md:text-3xl lg:text-4xl text-foreground mb-3"
        >
          {t('landing.choose_experience')}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-muted-foreground text-sm md:text-base max-w-xl mx-auto"
        >
          {t('landing.choose_experience_subtitle')}
        </motion.p>
      </div>

      {/* Marquee wrapper */}
      <div
        className="relative overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Left fade */}
        <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 z-10 pointer-events-none bg-gradient-to-r from-background to-transparent" />
        {/* Right fade */}
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 z-10 pointer-events-none bg-gradient-to-l from-background to-transparent" />

        <div
          ref={trackRef}
          className="marquee-track"
          data-paused={isPaused}
          style={{ '--marquee-duration': `${duration}s` } as CSSProperties}
        >
          {items.map((exp, i) => (
            <Link
              key={`${exp.slug}-${i}`}
              to={exp.slug === "special-events" ? "/learn-more" : `/events?category=${exp.slug}`}
              className="group flex flex-col items-center gap-1 rounded-lg border border-border/35 bg-card/55 backdrop-blur-sm px-2 py-2 md:px-2.5 md:py-2.5 hover:border-primary/35 hover:bg-card/80 transition-all duration-300 shrink-0 w-[84px] md:w-[100px] mx-[5px]"
            >
              <div className="p-1 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                <exp.icon className="h-3 w-3 md:h-3.5 md:w-3.5 text-primary" />
              </div>
              <span className="font-semibold text-[9px] md:text-[11px] text-foreground text-center leading-tight">{t(exp.labelKey)}</span>
              <span className="text-[8px] md:text-[9px] text-muted-foreground text-center leading-tight">{t(exp.taglineKey)}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExperienceNav;
